import { Menu, X, Phone, Mail, MapPin, User, LogOut, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useLocation, Link } from "wouter";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";

export default function Navigation() {
  const [location] = useLocation();
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  const isActive = (path: string) => {
    return location === path;
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'forex_manager': return 'Forex Manager';
      case 'travel_manager': return 'Travel Manager';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'forex_manager': return 'bg-green-100 text-green-800';
      case 'travel_manager': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-gradient-to-r from-orange-500 to-purple-600 text-white py-1 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                <span>+91 88826 40963</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                <span>support@travelwingsonline.in</span>
              </div>
            </div>
            <div className="hidden md:block">
              <span>Mon-Sat: 9AM-6PM IST</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link to="/" className="flex items-center">
                  <img
                    src="/logo.png"
                    alt="Travel Wings Logo"
                    className="h-12 w-auto"
                    width={160}
                    height={48}
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                  />
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="ml-6 flex items-center space-x-3">
                <Link 
                  to="/"
                  className={`transition-colors px-3 py-2 text-sm font-semibold border-b-2 ${
                    isActive('/') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  Home
                </Link>
                <Link 
                  to="/about"
                  className={`transition-colors px-2 py-2 text-sm font-medium border-b-2 ${
                    isActive('/about') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  About
                </Link>
                <Link 
                  to="/services"
                  className={`transition-colors px-2 py-2 text-sm font-medium border-b-2 ${
                    isActive('/services') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  Services
                </Link>
                <Link 
                  to="/packages"
                  className={`transition-colors px-2 py-2 text-sm font-medium border-b-2 ${
                    isActive('/packages') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  Packages
                </Link>
                <Link 
                  to="/team"
                  className={`transition-colors px-2 py-2 text-sm font-medium border-b-2 ${
                    isActive('/team') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  Our Team
                </Link>
                <Link 
                  to="/contact"
                  className={`transition-colors px-2 py-2 text-sm font-medium border-b-2 ${
                    isActive('/contact') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  Contact
                </Link>
                <Link 
                  to="/careers"
                  className={`transition-colors px-2 py-2 text-sm font-medium border-b-2 ${
                    isActive('/careers') 
                      ? 'text-orange-600 border-orange-600' 
                      : 'text-gray-700 hover:text-orange-600 border-transparent hover:border-orange-600'
                  }`}
                >
                  Careers
                </Link>
                <Link 
                  to="/live-rates"
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2 text-sm font-semibold rounded-md transition-colors border-b-2 border-transparent"
                >
                  Live Rates
                </Link>

                {/* Auth Section - Desktop */}
                {isAuthenticated && user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Open account menu"
                        className="flex items-center space-x-2 h-10 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: '#ea580c'}}>
                          <span className="text-white text-sm font-semibold">
                            {(user.firstName || user.username || user.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <ChevronDown className="h-4 w-4 text-gray-600" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-white border border-gray-200 shadow-lg rounded-md">
                      <div className="px-3 py-2 bg-white">
                        <p className="text-sm font-medium text-black">{user.firstName || user.username}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <Badge className={`${getRoleColor(user.role)} text-xs mt-1`}>
                          {getRoleDisplay(user.role)}
                        </Badge>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center w-full px-3 py-2 text-sm text-black hover:bg-gray-100">
                          <User className="mr-2 h-4 w-4" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <button onClick={logout} className="flex items-center w-full px-3 py-2 text-sm text-black hover:bg-gray-100">
                          <LogOut className="mr-2 h-4 w-4" />
                          Logout
                        </button>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link to="/login" className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-gray-50 rounded-md transition-colors">
                    <User className="h-4 w-4" />
                    <span>Login</span>
                  </Link>
                )}
              </div>
            </div>

            <div className="lg:hidden flex items-center">
              {/* Mobile Auth Section */}
              {isAuthenticated && user ? (
                <div className="flex items-center space-x-2 mr-3">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {(user.firstName || user.username || user.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-gray-50 rounded-md transition-colors mr-2">
                  <User className="h-4 w-4" />
                  <span>Login</span>
                </Link>
              )}

              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-gray-700 hover:text-orange-600"
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <X className="h-6 w-6" aria-hidden />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t shadow-lg">
              <Link 
                to="/"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-semibold w-full text-left ${
                  isActive('/') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/about"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${
                  isActive('/about') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                About Us
              </Link>
              <Link 
                to="/services"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${
                  isActive('/services') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                Services
              </Link>
              <Link 
                to="/packages"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${
                  isActive('/packages') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                Packages
              </Link>
              <Link 
                to="/team"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${
                  isActive('/team') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                Our Team
              </Link>
              <Link 
                to="/contact"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${
                  isActive('/contact') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                Contact
              </Link>
              <Link 
                to="/careers"
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${
                  isActive('/careers') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                }`}
              >
                Careers
              </Link>
              <Link 
                to="/live-rates"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-3 rounded-md text-base font-medium w-full text-center mt-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              >
                Live Rates
              </Link>

              {/* Mobile Auth Section */}
              <div className="border-t pt-4 mt-6">
                {isAuthenticated && user ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 px-3 py-2">
                      <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-lg font-semibold">
                          {(user.firstName || user.username || user.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {user.firstName || user.username}
                        </p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <Badge className={`${getRoleColor(user.role)} text-xs mt-1`}>
                          {getRoleDisplay(user.role)}
                        </Badge>
                      </div>
                    </div>
                    <Link 
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center px-3 py-3 rounded-md text-base font-medium w-full ${
                        isActive('/dashboard') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                      }`}
                    >
                      <User className="mr-3 h-5 w-5" />
                      Dashboard
                    </Link>
                    <Button 
                      variant="outline" 
                      className="w-full mx-3" 
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <div className="px-3">
                    <Link 
                      to="/login" 
                      onClick={() => setIsOpen(false)} 
                      className="flex items-center justify-center w-full px-4 py-3 text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors"
                    >
                      <User className="mr-2 h-4 w-4" />
                      Login
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}